import * as Sentry from "@sentry/react";
import { StrictMode, useEffect, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.jsx'
import LoadingCard from './utils/LoadingCard.jsx'
import './index.css'
import '@mantine/core/styles.css';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Mask all text and inputs by default to protect patient PII
      maskAllText: true,
      blockAllMedia: false,
    }),
  ],

  // Capture 100% of errors in all environments
  // Transactions: 100% dev, reduce to 0.1 in production via VITE_SENTRY_TRACES_SAMPLE_RATE
  tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "1.0"),

  // Session Replay: record 10% of normal sessions, 100% of sessions that hit an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

import { createTheme, MantineProvider, rem } from '@mantine/core';
import { createBrowserRouter, RouterProvider, useFetcher } from 'react-router-dom';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
import { NavbarNested } from './components/Admin/Navigation/DesktopNav/NavbarNested.jsx';
import { AppContextProvider, useAppContext } from './components/context/AppContext.jsx';
import authenticate from './utils/Authenticate.jsx';
import axiosInstance from './axios.js';
import AuthProvider from './utils/AuthProvider.jsx';
import ProtectedRoute from './utils/ProtectedRoute.jsx';
import { Notifications } from '@mantine/notifications';
import { LoadingProvider } from './components/context/LoadingContext.jsx';
// import DealerUpload from './utils/DealerUpload.jsx';
// import ProductUpload from './utils/ProductsUpload.jsx';
// import HoneyRecommendationChat from './components/Chat/HoneyRecommendationChat.jsx';

// Route-level pages are code-split via React.lazy() instead of being bundled eagerly - previously
// every page in the app (~50 components) was pulled into one JS chunk that had to fully download
// and parse before even the "/" redirect stub could run, which dominated pageload time app-wide.
const Home = lazy(() => import('./components/Patient/Dashboard/Home.jsx'));
const AdminHome = lazy(() => import('./components/Admin/Dashboard/Home.jsx'));
const DealerHome = lazy(() => import('./components/Dealer/Dashboard/Home.jsx'));
const Form = lazy(() => import('./components/Patient/Registration/Form.jsx'));
const Appointment = lazy(() => import('./components/Admin/Appointments/Appointment.jsx'));
const Template = lazy(() => import('./components/Admin/Appointments/Template.jsx'));
const DosageUpload = lazy(() => import('./utils/DosageUpload.jsx'));
const Dosage = lazy(() => import('./components/Admin/Dosages/Dosage.jsx'));
const Login = lazy(() => import('./components/Authentication/Login.jsx'));
const Logout = lazy(() => import('./components/Authentication/Logout.jsx'));
const StaffManagement = lazy(() => import('./components/Admin/Staff/StaffManagement.jsx'));
const InstructionUpload = lazy(() => import('./utils/InstructionUpload.jsx'));
const OpenSlots = lazy(() => import('./components/Admin/Slots/OpenSlots.jsx'));
const EditSlots = lazy(() => import('./components/Admin/Slots/EditSlots.jsx'));
const Slots = lazy(() => import('./components/Admin/Slots/Slots.jsx'));
const Schedule = lazy(() => import('./components/Patient/Appointments/Schedule.jsx'));
const EditSchedule = lazy(() => import('./components/Patient/Appointments/EditSchedule.jsx'));
const Profile = lazy(() => import('./components/Patient/Profile/Profile.jsx'));
const Notification = lazy(() => import('./components/Admin/Notifications/Notification.jsx'));
const View = lazy(() => import('./components/Patient/Appointments/View.jsx'));
const Patient = lazy(() => import('./components/Admin/Patients/Patient.jsx'));
const NotFound = lazy(() => import('./utils/NotFound.jsx'));
const AdminProfile = lazy(() => import('./components/Admin/Profile/AdminProfile.jsx'));
const Centre = lazy(() => import('./components/Admin/Centre/Centre.jsx'));
const PatientAppointments = lazy(() => import('./components/Admin/Patients/PatientAppointments.jsx'));
const GeneralInstructions = lazy(() => import('./components/Admin/GeneralInstructions/GeneralInstructions.jsx'));
const Dealer = lazy(() => import('./components/Admin/Dealers/Dealer.jsx'));
const DealerAnalytics = lazy(() => import('./components/Admin/Dealers/DealerAnalytics.jsx'));
const DealerDrilldown = lazy(() => import('./components/Admin/Dealers/DealerDrilldown.jsx'));
const Product = lazy(() => import('./components/Admin/Products/Product.jsx'));
const CreateOrder = lazy(() => import('./components/Dealer/Order/CreateOrder.jsx'));
const DealerOrders = lazy(() => import('./components/Dealer/Order/Orders.jsx'));
const Order = lazy(() => import('./components/Admin/Orders/Order.jsx'));
const EditOrder = lazy(() => import('./components/Dealer/Order/EditOrder.jsx'));
const ChangePassword = lazy(() => import('./components/Dealer/Profile/ChangePassword.jsx'));
const NotificationCard = lazy(() => import('./components/Patient/Notification/NotificationCard.jsx'));
const ConsultedAppointments = lazy(() => import('./components/Admin/Appointments/ConsultedAppointments.jsx'));
const PaymentDetails = lazy(() => import('./components/Patient/Payment/PaymentDetails.jsx'));
const AppointmentCalendarDemo = lazy(() => import('./components/Admin/Appointments/Calendar.jsx'));
const AppUsageGuide = lazy(() => import('./components/Patient/Notification/AppUsageGuide.jsx'));
const VideoUpload = lazy(() => import('./components/Admin/Video/VideoUpload.jsx'));
const DealerVideoList = lazy(() => import('./components/Dealer/Video/Video.jsx'));
const OrdersDashboard = lazy(() => import('./components/Admin/Orders/Order.jsx'));
const FollowUpAppointments = lazy(() => import('./components/Admin/Appointments/FollowUpAppointments.jsx'));


const theme = createTheme({
  colors: {
    'color1': ['#C29F00', '#6F3D1B', '#6F3D1B', '#2AC9DE', '#1AC2D9', '#11B7CD', '#09ADC3', '#0E99AC', '#128797', '#147885'],
    'color2': ['#6F3D1B', '#6F3D1B', '#6F3D1B', '#6F3D1B', '#6F3D1B', '#6F3D1B', '#6F3D1B', '#6F3D1B', '#6F3D1B', '#6F3D1B'],
    'color3': ['#C6C3C1', '#6F3D1B', '#EC7CC3', '#ED5DB8', '#F13EAF', '#F71FA7', '#FF00A1', '#E00890', '#C50E82', '#AD1374'],
    'color4': ['#D1A774', '#6F3D1B', '#EC7CC3', '#ED5DB8', '#F13EAF', '#F71FA7', '#FF00A1', '#E00890', '#C50E82', '#AD1374']
  },
  primaryColor:'color2',
  primaryShade:0,
});

  const router = sentryCreateBrowserRouter([
    {
      path: "/admin",
      element:
      <AuthProvider>
      <ProtectedRoute
        requiredRole={["admin","staff"]}>
        <LoadingProvider>
          <App />
        </LoadingProvider>
      </ProtectedRoute>,
      </AuthProvider>,
      children: [
        // Baseline screens - no requiredPermission, every admin-side account gets these
        {path: "", element: <Home />},
        {path:"dashboard", element: <AdminHome/> },
        {path:"profile",element: <AdminProfile />},
        {path:"notification",element: <Notification />},
        {path: "calendar", element: <AppointmentCalendarDemo />}, // not linked from any nav; backend still full-admin-only
        {path: "form", element: <ProtectedRoute requiredPermission="emergencyForm"><Form /></ProtectedRoute>},
        {path:"dosages",element: <ProtectedRoute requiredPermission="dosages"><Dosage /></ProtectedRoute>},
        {path:"dosageUpload",element: <ProtectedRoute requiredPermission="dosages"><DosageUpload /></ProtectedRoute>},
        {path:"dealers",element: <ProtectedRoute requiredPermission="dealers"><Dealer /></ProtectedRoute>},
        {path:"dealer-analytics",element: <ProtectedRoute requiredPermission="dealerAnalytics"><DealerAnalytics /></ProtectedRoute>},
        {path:"dealers/analytics/:id",element: <ProtectedRoute requiredPermission="dealerAnalytics"><DealerDrilldown /></ProtectedRoute>},
        {path:"orders",element: <ProtectedRoute requiredPermission="orders"><OrdersDashboard /></ProtectedRoute>},
        {path:"products",element: <ProtectedRoute requiredPermission="products"><Product /></ProtectedRoute>},
        {path:"instructions",element: <ProtectedRoute requiredPermission="instructions"><GeneralInstructions /></ProtectedRoute>},
        {path:"instructionUpload",element: <ProtectedRoute requiredPermission="instructions"><InstructionUpload /></ProtectedRoute>},
        {path:"centres",element: <ProtectedRoute requiredPermission="centres"><Centre /></ProtectedRoute>},
        {path:"videoUpload",element: <ProtectedRoute requiredPermission="videoUpload"><VideoUpload /></ProtectedRoute>},
        // {path:"dealerUpload",element: <DealerUpload />},
        // {path:"productUpload",element: <ProductUpload />},
        {path:"staff",element: <ProtectedRoute requiredPermission="manageUsers"><StaffManagement /></ProtectedRoute>},
        {path:"patients",
          children:[
            {path: "",element: <ProtectedRoute requiredPermission="patients"><Patient /></ProtectedRoute>},
            {path: ":id", element: <ProtectedRoute requiredPermission="patients"><PatientAppointments /></ProtectedRoute>},
        ]
        },
        {path:"slots",
          children: [
            {path: "open", element: <ProtectedRoute requiredPermission="slots"><OpenSlots /></ProtectedRoute>},
            // {path: "edit", element: <EditSlots />},
            {path: "view", element: <ProtectedRoute requiredPermission="slots"><Slots /></ProtectedRoute>},
          ]
        },
        {path:"appointments",
          children: [
            {path: "",element: <ProtectedRoute requiredPermission="appointments"><Appointment /></ProtectedRoute>},
            {path: ":id", element: <ProtectedRoute requiredPermission="appointments"><Template /></ProtectedRoute>},
            {path: "editSchedule/:id", element: <ProtectedRoute requiredPermission="appointments"><EditSchedule /></ProtectedRoute>},
          ]
        },
        {path:"followups",element: <ProtectedRoute requiredPermission="followups"><FollowUpAppointments /></ProtectedRoute>},
        {path:"consultations",element: <ProtectedRoute requiredPermission="consultations"><ConsultedAppointments /></ProtectedRoute>},
        {path:"*",element: <NotFound />}
      ]
    },
   {
      path: "/patient",
      element: 
      <AuthProvider>
      <ProtectedRoute
        requiredRole="patient">
        <LoadingProvider> 
          <App />
        </LoadingProvider>
      </ProtectedRoute>
      </AuthProvider>,
      children: [
        {path: "dashboard", element: <Home />},
        {path: "form", element: <Form />},
        {path: "appointments/schedule/:id", element: <Schedule />},
        {path: "appointments/editSchedule/:id", element: <EditSchedule />},
        {path: "appointments/view/:id", element: <View />},
        {path: "notifications",element: <NotificationCard />},
        {path: "info",element: <AppUsageGuide />},
        {path: "profile",element: <Profile />},
        {path: "payment",element: <PaymentDetails />},
        {path:"*",element: <NotFound />}
      ]
    },
    {
      path: "/dealer",
      element: 
      <AuthProvider>
      <ProtectedRoute
        requiredRole="dealer">
        <LoadingProvider> 
          <App />
        </LoadingProvider>
      </ProtectedRoute>
      </AuthProvider>,
      children: [
        {path: "dashboard", element: <DealerOrders />},
        {path: "form", element: <CreateOrder />},
        {path: "orders", element: <DealerOrders />},
        {path:"products",element: <Product />},
        {path:"communication",element: <DealerVideoList />},
        {path:"order/edit/:id",element: <EditOrder />},
        {path:"change-password",element: <ChangePassword />},
        // {path:"*",element: <NotFound />}
      ]
    },
    {
      path: "/",
      element: <AuthProvider><App /></AuthProvider>
    },
    {
      path: "/form",
      element:<Form />
    },
    // {
    //   path: "/chat-mhs",
    //   element: 
    //   <HoneyRecommendationChat /> 
    // },
    {
      path: "/login",
      element: 
      <AuthProvider><Login /></AuthProvider>
    },
    {
      path:'/logout',
      element: <Logout />
    }
  ])
  createRoot(document.getElementById('root')).render(
     <StrictMode>
      <MantineProvider theme={theme} withGlobalStyles withNormalizeCSS>
      <Notifications position='top-center' zIndex={100000}/>
      <AppContextProvider>
      {/* <AuthProvider> */}
      <Suspense fallback={<LoadingCard/>}>
        <RouterProvider router={router} >
        </RouterProvider>
      </Suspense>
        {/* </AuthProvider> */}
      </AppContextProvider>
      </MantineProvider>
     </StrictMode> 
  )
  // return <RouterProvider router={router} />;



